import flatten from 'lodash/flatten';
import uniq from 'lodash/uniq';

import { EntryDetailData, EntryListItem, EntryListQuery } from 'chaika-frontend-types/frontend-cms';

import { store } from '@store';
import { labEntryTypeIdsSet } from '@store/types';
import { EntryApi } from '@api/EntryApi';
import { entryTypeService } from '@template/services/entryType/entryTypeService';
import { serviceService } from '@services/services/serviceService';
import { EntryList, EntryListParams } from '@patient/store/ehr/models/EntryList';
import { getUniqChunkedIds } from '@utils/getUniqChunkedIds';
import { translate } from '@chaika/i18n/helper';
import { patientService } from '@patient/services/patient/patientService';

export interface LoadEntryListOptions {
    withEntryTypeTranslations?: boolean;
}

class EntryListService {
    public async loadEntryList(
        entryList: EntryList,
        apiParams: EntryListQuery,
        options: LoadEntryListOptions = {},
    ): Promise<void> {
        const entriesListCreateParams = await this.loadEntryListData(entryList, apiParams, options);

        entryList.set(entriesListCreateParams);
    }

    public async addToEntryList(entryList: EntryList, apiParams: EntryListQuery): Promise<void> {
        const entriesListCreateParams = await this.loadEntryListData(entryList, apiParams);

        entryList.add(entriesListCreateParams);
    }

    private async loadEntryListData(
        entryList: EntryList,
        apiParams: EntryListQuery,
        options: LoadEntryListOptions = {},
    ): Promise<EntryListParams[]> {
        const [entries] = await Promise.all([EntryApi.getList(apiParams)]);
        const labEntryIds = entries.filter(({ typeId }) => labEntryTypeIdsSet.has(typeId)).map(({ id }) => id);

        const chunked = getUniqChunkedIds(labEntryIds);
        const details =
            chunked.length > 0
                ? flatten(await Promise.all(chunked.map(ids => EntryApi.getEntriesDetailsList(ids))))
                : [];

        entryList.setEntryDetails(details);

        await Promise.all([this.loadEntryData(entries, options), this.loadEntryDetailData(details)]);

        return entries.map(entry => {
            const entryType = store.entryTypeList.getVersion(entry.typeId, entry.typeVersion);

            return {
                ...entry,
                entryTypeType: entryType ? entryType.type : '',
                entryTypeTitle: entryType ? entryType.title : '',
                statusTitle: translate(entry.status),
            };
        });
    }

    private async loadEntryData(entries: EntryListItem[], options: LoadEntryListOptions): Promise<void> {
        const ehrIds: string[] = [];
        const entryTypeIds: string[] = [];

        entries.forEach(({ typeId, ehrId }) => {
            entryTypeIds.push(typeId);
            ehrIds.push(ehrId);
        });

        await Promise.all([
            patientService.loadByIds(uniq(ehrIds)),
            entryTypeService.loadByIds(entryTypeIds, { withTranslations: options.withEntryTypeTranslations }),
        ]);
    }

    private async loadEntryDetailData(entries: EntryDetailData[]): Promise<void> {
        const serviceIds = new Set(
            flatten(
                entries.map(({ laboratoryServiceIds = [], serviceIds = [] }) => [
                    ...laboratoryServiceIds,
                    ...serviceIds,
                ]),
            ),
        );
        await serviceService.loadByIds(Array.from(serviceIds.values()));
    }
}

export const entryListService = new EntryListService();
